/**
 * branchPersonnelAssignmentDataService — sube-personel atama verisi icin SQL-only adaptor.
 *
 * Bu servis, eski Firebase RTDB `companies/{cid}/branch_personnel_assignments`
 * dugumunun yerini alir. Artik TUM okuma/yazma DAIMA dogrudan SQL'e gider
 * (companyId, apiClient interceptor tarafindan X-Company-Id header'inda gonderilir —
 * body/query'de companyId GONDERILMEZ).
 *
 * KRITIK: branchId, kanonik sube kimligidir (= Firebase RTDB key = SQL Branch.FirebaseId).
 *         userId ise kullanicinin Firebase Auth uid'sidir.
 */

import { mainApi } from '../../config/apiClient';

export interface BranchPersonnelAssignment {
  id: number;
  branchId: string;
  userId: string;
  assignedRole: string;
}

function mapAssignment(a: any): BranchPersonnelAssignment {
  return {
    id: Number(a?.id ?? 0),
    branchId: String(a?.branchId ?? ''),
    userId: String(a?.userId ?? ''),
    assignedRole: String(a?.assignedRole ?? ''),
  };
}

/**
 * Belirli bir kullaniciya ait tum sube atamalarini getirir (SADECE SQL).
 * Eski RTDB `orderByChild('userId').equalTo(uid)` sorgusunun karsiligi.
 */
export async function getAssignmentsByUser(userId: string): Promise<BranchPersonnelAssignment[]> {
  const res = await mainApi.get('/api/branch-personnel-assignments/by-user', { params: { userId } });
  const items = Array.isArray(res.data?.items) ? res.data.items : [];
  return items.map(mapAssignment);
}

/**
 * Belirli bir subeye ait tum personel atamalarini getirir (SADECE SQL).
 * Eski RTDB `orderByChild('branchId').equalTo(branchId)` sorgusunun karsiligi.
 */
export async function getAssignmentsByBranch(branchId: string): Promise<BranchPersonnelAssignment[]> {
  const res = await mainApi.get('/api/branch-personnel-assignments/by-branch', { params: { branchId } });
  const items = Array.isArray(res.data?.items) ? res.data.items : [];
  return items.map(mapAssignment);
}

/**
 * Bir kullaniciyi bir subeye ek yonetici olarak atar (SADECE SQL).
 * Eski RTDB existence-check + push/set akisinin karsiligi.
 * Donus: atama zaten var miydi (`alreadyExists`) ve atama kaydinin id'si.
 */
export async function assignManagerToBranch(
  branchId: string,
  userId: string,
  assignedRole = 'manager'
): Promise<{ alreadyExists: boolean; id: number }> {
  const res = await mainApi.post('/api/branch-personnel-assignments/assign', { branchId, userId, assignedRole });
  return { alreadyExists: !!res.data?.alreadyExists, id: res.data?.id };
}

/**
 * Bir kullanicinin bir subedeki atamasini kaldirir (SADECE SQL).
 * Eski RTDB silme akisinin ve Node `dotnetBridge.removeBranchPersonnelAssignment` koprusunun karsiligi.
 * Donus: silinen kayit sayisi (`removedCount`).
 */
export async function removeAssignment(
  branchId: string,
  userId: string
): Promise<{ removedCount: number }> {
  const res = await mainApi.post('/api/branch-personnel-assignments/remove', { branchId, userId });
  return { removedCount: Number(res.data?.removedCount ?? 0) };
}
